import type { ModalAnswers } from "../../hooks/useB2CProfileModals";
import { useClientTranslations } from "../../lib/i18n/client";
import { profileSections } from "../../lib/profile/b2cProfileSections";
import type { OnboardingAnswers } from "../../types/onboarding";
import { AdditionalInfoQuestion } from "../onboarding/questions/AdditionalInfoQuestion";
import { FertilityJourneyTypesQuestion } from "../onboarding/questions/FertilityJourneyTypesQuestion";
import { FertilityRoleQuestion } from "../onboarding/questions/FertilityRoleQuestion";
import { FertilityStatusQuestion } from "../onboarding/questions/FertilityStatusQuestion";
import { SelectProviderQuestion } from "../onboarding/questions/SelectProviderQuestion";
import { SetupProfileDetailsQuestion } from "../onboarding/questions/SetupProfileDetailsQuestion";
import { SetupProfileQuestion } from "../onboarding/questions/SetupProfileQuestion";
import { ChangePasswordModal } from "./ChangePasswordModal";
import { DeleteAccountModal } from "./DeleteAccountModal";
import { ProfileModal } from "./ProfileModal";

interface ProfileModalsProps {
	openModalId: string | null;
	modalAnswers: ModalAnswers;
	onClose: () => void;
	onInputChange: (modalId: string, field: string, value: unknown) => void;
	onSave: (modalId: string) => void;
}

export function B2CProfileModals({
	openModalId,
	modalAnswers,
	onClose,
	onInputChange,
	onSave,
}: ProfileModalsProps) {
	const { t: tProfile } = useClientTranslations("profile");
	const { t: tOnboarding } = useClientTranslations("onboarding");

	const renderQuestionComponent = (section: (typeof profileSections)[0]) => {
		// Get the answers for this specific section
		const sectionAnswers = modalAnswers[section.id as keyof ModalAnswers] || {};

		const props = {
			question: section.question,
			answers: sectionAnswers as OnboardingAnswers,
			onInputChange: (field: string, value: unknown) =>
				onInputChange(section.id, field, value),
		};

		switch (section.componentType) {
			case "SetupProfile":
				return <SetupProfileQuestion {...props} />;
			case "SetupProfileDetails":
				return <SetupProfileDetailsQuestion {...props} />;
			case "FertilityStatus":
				return <FertilityStatusQuestion {...props} />;
			case "FertilityRole":
				return <FertilityRoleQuestion {...props} />;
			case "FertilityJourneyTypes":
				return <FertilityJourneyTypesQuestion {...props} />;
			case "AdditionalInfo":
				return <AdditionalInfoQuestion {...props} />;
			case "ClinicProvider":
				return <SelectProviderQuestion {...props} />;
			case "ChangePassword":
				return null;
			default:
				return null;
		}
	};

	return (
		<>
			{profileSections.map((section) => {
				return (
					<ProfileModal
						key={section.id}
						isOpen={openModalId === section.id}
						onClose={onClose}
						title={
							section.titleKey === "changePassword"
								? tProfile(section.titleKey)
								: tOnboarding(section.titleKey)
						}
						onSave={() => onSave(section.id)}
						saveButtonText={tProfile("common.save")}
						hideSaveButton={false}
					>
						{renderQuestionComponent(section)}
					</ProfileModal>
				);
			})}

			{/* Shared Change Password Modal */}
			<ChangePasswordModal
				isOpen={openModalId === "change-password"}
				onClose={onClose}
			/>

			{/* Delete Account Modal */}
			<DeleteAccountModal
				isOpen={openModalId === "delete-account"}
				onClose={onClose}
			/>
		</>
	);
}
